import { useState, useEffect } from 'react'
import type React from 'react'
import { StudentShell } from '@/components/shared/StudentShell'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import {
  Coins, ArrowDownLeft, ArrowUpRight, TrendingUp, Clock,
  FileCheck, History, Gift, CreditCard, CheckCircle2, AlertCircle,
  Loader2, Award, CalendarCheck
} from 'lucide-react'

interface Transaction {
  id: number
  amount: number
  kind: string
  description: string
  created_at: string
}

interface WalletStats {
  total_earned: number
  total_rewards?: number
  total_purchased?: number
  total_spent: number
  tests_unlocked_count: number
  daily_grants_count: number
}

interface Props {
  balance: number
  can_request_today: boolean
  razorpay_key_id?: string
  user_info?: {
    name: string
    email: string
    phone?: string
  }
  stats: WalletStats
  transactions: Transaction[]
}

declare global {
  interface Window {
    Razorpay: any
  }
}

const PRESET_AMOUNTS = [50, 100, 250, 500, 1000]

const KIND_ICONS: Record<string, React.ComponentType<{ className?: string }>> = {
  grant: ArrowDownLeft,
  spend: ArrowUpRight,
  refund: ArrowDownLeft,
  bonus: Award,
  topup: CreditCard,
}

const KIND_COLORS: Record<string, string> = {
  grant: 'bg-primary/10 text-primary border-primary/20',
  spend: 'bg-destructive/10 text-destructive border-destructive/20',
  refund: 'bg-tertiary/10 text-tertiary border-tertiary/20',
  bonus: 'bg-secondary/10 text-secondary border-secondary/20',
  topup: 'bg-primary/15 text-primary border-primary/30',
}

export default function CreditsIndex({
  balance,
  can_request_today,
  razorpay_key_id,
  user_info,
  stats,
  transactions = []
}: Props) {
  const [requesting, setRequesting] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  const [currentBalance, setCurrentBalance] = useState(balance)

  // Top-up state
  const [selectedAmount, setSelectedAmount] = useState<number>(100)
  const [customAmount, setCustomAmount] = useState<string>('')
  const [isProcessingPayment, setIsProcessingPayment] = useState(false)
  const [paymentStatus, setPaymentStatus] = useState<{ success: boolean; message: string } | null>(null)

  // Load Razorpay script on mount
  useEffect(() => {
    if (typeof window !== 'undefined' && !window.Razorpay) {
      const script = document.createElement('script')
      script.src = 'https://checkout.razorpay.com/v1/checkout.js'
      script.async = true
      document.body.appendChild(script)
    }
  }, [])

  const effectiveAmount = customAmount ? parseInt(customAmount, 10) : selectedAmount

  const handleTopup = async () => {
    if (!effectiveAmount || effectiveAmount < 1) {
      setPaymentStatus({ success: false, message: 'Please enter a valid amount (minimum ₹1)' })
      return
    }

    setIsProcessingPayment(true)
    setPaymentStatus(null)

    try {
      // 1. Create order on backend
      const res = await fetch('/student/payments', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement)?.content || ''
        },
        body: JSON.stringify({ amount: effectiveAmount })
      })

      const orderData = await res.json()
      if (!orderData.success) {
        setPaymentStatus({ success: false, message: orderData.error || 'Failed to create payment order' })
        setIsProcessingPayment(false)
        return
      }

      // 2. Configure and open Razorpay Checkout
      const options = {
        key: orderData.key_id || razorpay_key_id,
        amount: orderData.amount,
        currency: orderData.currency || 'INR',
        name: 'PrepKiya',
        description: `Wallet Top-up (${orderData.credits} Credits)`,
        order_id: orderData.order_id,
        prefill: {
          name: user_info?.name || '',
          email: user_info?.email || '',
          contact: user_info?.phone || ''
        },
        method: {
          upi: true,
          card: true,
          netbanking: true,
          wallet: true,
        },
        theme: {
          color: 'hsl(var(--primary))'
        },
        handler: async (response: {
          razorpay_payment_id: string
          razorpay_order_id: string
          razorpay_signature: string
        }) => {
          try {
            // 3. Verify payment signature on backend
            const verifyRes = await fetch('/student/payments/verify', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'X-CSRF-Token': (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement)?.content || ''
              },
              body: JSON.stringify({
                razorpay_order_id: response.razorpay_order_id,
                razorpay_payment_id: response.razorpay_payment_id,
                razorpay_signature: response.razorpay_signature
              })
            })

            const verifyData = await verifyRes.json()
            if (verifyData.success) {
              setCurrentBalance(verifyData.balance)
              setPaymentStatus({
                success: true,
                message: `🎉 Payment Successful! Added ${verifyData.credits_granted || effectiveAmount} credits to your wallet (Payment ID: ${response.razorpay_payment_id}). Notification sent.`
              })
              setTimeout(() => window.location.reload(), 2500)
            } else {
              setPaymentStatus({
                success: false,
                message: verifyData.error || 'Payment verification failed'
              })
            }
          } catch (err: any) {
            setPaymentStatus({ success: false, message: err.message || 'Payment verification error' })
          } finally {
            setIsProcessingPayment(false)
          }
        },
        modal: {
          ondismiss: () => {
            setIsProcessingPayment(false)
          }
        }
      }

      if (window.Razorpay) {
        const rzp = new window.Razorpay(options)
        rzp.open()
      } else {
        setPaymentStatus({ success: false, message: 'Payment gateway SDK is loading. Please try again in a moment.' })
        setIsProcessingPayment(false)
      }
    } catch (err: any) {
      setPaymentStatus({ success: false, message: err.message || 'Payment initiation failed' })
      setIsProcessingPayment(false)
    }
  }

  const handleRequest = async () => {
    setRequesting(true)
    setMessage(null)
    try {
      const res = await fetch('/student/credit_requests', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': (document.querySelector('meta[name=csrf-token]') as HTMLMetaElement)?.content || ''
        }
      })
      const data = await res.json()
      if (data.success) {
        setCurrentBalance(data.balance)
        setMessage(data.message)
        setTimeout(() => window.location.reload(), 1500)
      } else {
        setMessage(data.error)
      }
    } finally {
      setRequesting(false)
    }
  }

  return (
    <StudentShell>
      <div className="space-y-6 sm:space-y-8 max-w-7xl mx-auto pb-16">
        {/* Hero Card Banner */}
        <div className="rounded-2xl border border-border/70 bg-card p-3.5 sm:p-6 lg:p-8 shadow-xs">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-5">
            <div className="space-y-2 max-w-2xl">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[10px] font-bold uppercase tracking-wider text-tertiary bg-tertiary/15 px-2.5 py-0.5 rounded-full border border-tertiary/25 inline-flex items-center gap-1.5 shadow-2xs">
                  <Coins className="h-3 w-3 text-tertiary" />
                  <span>PrepKiya Wallet</span>
                </span>
              </div>

              <h1 className="text-lg sm:text-2xl font-bold text-foreground tracking-tight">
                Wallet & Practice Credits
              </h1>

              <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                Earn daily free credits to unlock premium full-length mock tests and previous year question papers.
              </p>
            </div>

            {/* Quick Metrics Strip */}
            <div className="grid grid-cols-3 gap-2 sm:gap-3 shrink-0 lg:max-w-md w-full">
              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-tertiary/10 border border-tertiary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{currentBalance}</span>
                <div className="flex items-center gap-1 mt-0.5">
                  <Coins className="h-3 w-3 text-tertiary" />
                  <span className="text-[10px] font-bold text-tertiary uppercase tracking-wider">Balance</span>
                </div>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-primary/10 border border-primary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-primary">+{stats.total_earned}</span>
                <span className="text-[10px] font-bold text-primary uppercase tracking-wider mt-0.5">Earned</span>
              </div>

              <div className="flex flex-col items-center sm:items-start justify-center py-2.5 px-1 sm:p-3.5 rounded-xl bg-secondary/10 border border-secondary/20 backdrop-blur-xs text-center sm:text-left">
                <span className="text-base sm:text-xl font-black text-foreground">{stats.tests_unlocked_count}</span>
                <span className="text-[10px] font-bold text-secondary uppercase tracking-wider mt-0.5">Unlocked</span>
              </div>
            </div>
          </div>
        </div>

        {/* 4 Wallet Stat Cards */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
          <Card className="border border-border bg-card shadow-glass-sm rounded-2xl p-3.5 sm:p-4 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider truncate">
                Total Earned
              </p>
              <div className="w-7 h-7 rounded-lg bg-primary/10 text-primary flex items-center justify-center border border-primary/20 shrink-0">
                <Gift className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-lg sm:text-2xl font-extrabold text-primary tracking-tight">
                +{stats.total_rewards ?? stats.total_earned}
              </div>
              <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate mt-0.5">
                Free, Gifts & Bonuses
              </p>
            </div>
          </Card>

          <Card className="border border-border bg-card shadow-glass-sm rounded-2xl p-3.5 sm:p-4 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider truncate">
                Utilized
              </p>
              <div className="w-7 h-7 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center border border-secondary/20 shrink-0">
                <TrendingUp className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <div className="flex items-center gap-1 text-lg sm:text-2xl font-extrabold text-foreground tracking-tight">
                {stats.total_spent}
              </div>
              <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate mt-0.5">
                Invested in Practice
              </p>
            </div>
          </Card>

          <Card className="border border-border bg-card shadow-glass-sm rounded-2xl p-3.5 sm:p-4 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider truncate">
                Tests Unlocked
              </p>
              <div className="w-7 h-7 rounded-lg bg-tertiary/10 text-tertiary flex items-center justify-center border border-tertiary/20 shrink-0">
                <FileCheck className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <p className="text-lg sm:text-2xl font-extrabold text-foreground tracking-tight">
                {stats.tests_unlocked_count}
              </p>
              <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate mt-0.5">
                Tests Owned
              </p>
            </div>
          </Card>

          <Card className="border border-border bg-card shadow-glass-sm rounded-2xl p-3.5 sm:p-4 flex flex-col justify-between space-y-2">
            <div className="flex items-center justify-between">
              <p className="text-[10px] sm:text-xs font-bold text-muted-foreground uppercase tracking-wider truncate">
                Daily Claims
              </p>
              <div className="w-7 h-7 rounded-lg bg-secondary/10 text-secondary flex items-center justify-center border border-secondary/20 shrink-0">
                <CalendarCheck className="h-3.5 w-3.5" />
              </div>
            </div>
            <div>
              <p className="text-lg sm:text-2xl font-extrabold text-foreground tracking-tight">
                {stats.daily_grants_count}
              </p>
              <p className="text-[10px] sm:text-[11px] text-muted-foreground font-medium truncate mt-0.5">
                Daily Rewards
              </p>
            </div>
          </Card>
        </div>

        {/* Razorpay Top-up Wallet Card */}
        <Card className="border border-border/70 bg-card rounded-2xl shadow-xs overflow-hidden relative">
          
          <CardHeader className="py-3.5 px-3.5 sm:px-6 border-b border-border/40 bg-muted/10">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div className="flex items-center gap-2.5">
                <div className="w-8 h-8 rounded-xl bg-primary/15 text-primary flex items-center justify-center border border-primary/25">
                  <CreditCard className="h-4 w-4" />
                </div>
                <div>
                  <CardTitle className="text-sm sm:text-base font-bold text-foreground">
                    Top Up Wallet with Razorpay
                  </CardTitle>
                  <p className="text-xs text-muted-foreground">
                    1 INR = 1 Credit. Use credits to unlock any mock test or PYQ paper instantly.
                  </p>
                </div>
              </div>
              <Badge variant="outline" className="w-fit text-[11px] font-bold border-primary/30 text-primary bg-primary/10">
                ⚡ Instant Deposit
              </Badge>
            </div>
          </CardHeader>

          <CardContent className="p-3.5 sm:p-6 space-y-4 sm:space-y-5">
            {paymentStatus && (
              <div className={`p-3 rounded-xl flex items-center gap-2.5 text-xs font-semibold border ${
                paymentStatus.success
                  ? 'bg-primary/10 text-primary border-primary/30'
                  : 'bg-destructive/10 text-destructive border-destructive/30'
              }`}>
                {paymentStatus.success ? (
                  <CheckCircle2 className="h-4 w-4 shrink-0" />
                ) : (
                  <AlertCircle className="h-4 w-4 shrink-0" />
                )}
                <span>{paymentStatus.message}</span>
              </div>
            )}

            {/* Quick preset buttons */}
            <div className="space-y-2">
              <label className="text-xs font-bold text-muted-foreground uppercase tracking-wider">
                Select Top-up Amount
              </label>
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 sm:gap-2.5">
                {PRESET_AMOUNTS.map((amt) => {
                  const isSelected = !customAmount && selectedAmount === amt
                  return (
                    <button
                      key={amt}
                      type="button"
                      onClick={() => {
                        setSelectedAmount(amt)
                        setCustomAmount('')
                      }}
                      className={`p-2.5 sm:p-3 rounded-xl border font-bold text-xs sm:text-sm transition-all flex flex-col items-center justify-center cursor-pointer ${
                        isSelected
                          ? 'border-primary bg-primary/15 text-primary shadow-xs'
                          : 'border-border bg-card/50 text-foreground hover:bg-muted/40'
                      }`}
                    >
                      <span>₹{amt}</span>
                      <span className="text-[10px] text-muted-foreground font-normal">
                        {amt} Credits
                      </span>
                    </button>
                  )
                })}
              </div>
            </div>

            {/* Custom Amount + Pay Button */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 pt-1">
              <div className="relative flex-1">
                <span className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground font-bold text-sm">
                  ₹
                </span>
                <input
                  type="number"
                  min="1"
                  placeholder="Or enter custom amount..."
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                  className="w-full pl-8 pr-4 py-2.5 rounded-xl border border-border bg-card text-foreground placeholder:text-muted-foreground text-xs sm:text-sm focus:outline-none focus:ring-2 focus:ring-primary/40 focus:border-primary font-medium [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                />
              </div>

              <Button
                size="lg"
                onClick={handleTopup}
                disabled={isProcessingPayment || !effectiveAmount || effectiveAmount < 1}
                className="font-bold h-11 px-6 rounded-xl shadow-md cursor-pointer bg-primary text-primary-foreground hover:bg-primary/90 shrink-0"
              >
                {isProcessingPayment ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Processing Payment...
                  </>
                ) : (
                  <>
                    <CreditCard className="mr-2 h-4 w-4" />
                    Pay ₹{effectiveAmount || 0} via Razorpay
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Daily Request CTA Box */}
        <Card className="border border-tertiary/30 bg-card rounded-2xl shadow-glass-sm overflow-hidden">
          <CardContent className="p-4 sm:p-6 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-4 sm:gap-6">
            <div className="space-y-1">
              <div className="flex items-center gap-2">
                <Gift className="h-5 w-5 text-tertiary shrink-0" />
                <h3 className="text-sm sm:text-base font-extrabold text-foreground">
                  Daily Credit Allowance (20 Credits)
                </h3>
              </div>
              <p className="text-xs text-muted-foreground leading-relaxed">
                All students receive 20 free credits every 24 hours. Credits are automatically deposited instantly.
              </p>
            </div>

            <div className="shrink-0 w-full sm:w-auto">
              {message && <p className="text-xs font-bold text-primary mb-2 text-center sm:text-left">{message}</p>}
              <Button
                variant="outline"
                size="lg"
                className={`w-full sm:w-auto font-bold h-11 px-5 rounded-xl shadow-xs text-xs sm:text-sm cursor-pointer ${
                  can_request_today
                    ? 'border-tertiary/40 text-tertiary hover:bg-tertiary/10'
                    : 'border-border text-muted-foreground'
                }`}
                disabled={!can_request_today || requesting}
                onClick={handleRequest}
              >
                {requesting ? (
                  'Depositing Credits...'
                ) : can_request_today ? (
                  <>
                    <Coins className="mr-2 h-4 w-4 text-tertiary" /> Claim 20 Free Credits Now
                  </>
                ) : (
                  <>
                    <Clock className="mr-2 h-4 w-4 text-muted-foreground" /> Claimed Today — Reset at Midnight
                  </>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Transaction History Section */}
        <Card className="rounded-2xl border border-border bg-card shadow-glass-sm overflow-hidden">
          <CardHeader className="py-3.5 px-4 sm:px-5 border-b border-border/40 bg-muted/20 flex flex-row items-center justify-between">
            <CardTitle className="text-xs sm:text-sm font-extrabold flex items-center gap-2 text-foreground">
              <History className="h-4 w-4 text-primary" /> Transaction Ledger
            </CardTitle>
            <Badge variant="outline" className="text-[10px] sm:text-[11px] font-bold border-border">
              {transactions.length} Entries
            </Badge>
          </CardHeader>
          <CardContent className="p-0">
            {transactions.length === 0 ? (
              <div className="py-12 text-center text-xs text-muted-foreground space-y-1.5">
                <Coins className="h-7 w-7 text-muted-foreground/30 mx-auto" />
                <p className="font-semibold text-foreground">No transactions recorded yet</p>
                <p className="text-[11px]">Your credit earnings and unlocks will appear here.</p>
              </div>
            ) : (
              <div className="divide-y divide-border/40">
                {transactions.map(t => {
                  const Icon = KIND_ICONS[t.kind] || ArrowDownLeft
                  const colorClass = KIND_COLORS[t.kind] || 'bg-muted text-muted-foreground border-border'
                  const isPositive = t.amount > 0

                  return (
                    <div
                      key={t.id}
                      className="flex items-center justify-between p-3.5 sm:p-4 hover:bg-muted/20 transition-colors gap-3"
                    >
                      <div className="flex items-center gap-3 min-w-0">
                        <div className={`w-9 h-9 rounded-xl flex items-center justify-center shrink-0 border ${colorClass}`}>
                          <Icon className="h-4 w-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-xs sm:text-sm font-bold text-foreground truncate">
                            {t.description || t.kind}
                          </p>
                          <p className="text-[10px] sm:text-xs text-muted-foreground mt-0.5 font-medium">
                            {t.created_at}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center gap-2.5 shrink-0">
                        <Badge variant="outline" className="text-[9px] font-bold uppercase tracking-wider hidden sm:inline-flex rounded-md border-border">
                          {t.kind}
                        </Badge>
                        <div className="flex items-center gap-1 font-extrabold">
                          <Coins className="h-3.5 w-3.5 text-tertiary shrink-0" />
                          <span className={`text-xs sm:text-sm ${isPositive ? 'text-primary' : 'text-destructive'}`}>
                            {isPositive ? `+${t.amount}` : t.amount}
                          </span>
                        </div>
                      </div>
                    </div>
                  )
                })}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </StudentShell>
  )
}
